/**
 * Precios del SaaS (fuente única para el configurador y la página de precios).
 * Fuente de verdad de negocio: negocio/Todosector-Brief-Negocio.md (§4).
 * Modelo: pack base mensual + módulos (8 €) y complementos (3 €, precio único)
 * à la carte (su precio viene del CMS). Operativos ILIMITADOS incluidos: solo
 * se cobra por usuario completo. El cobro anual = mensual × `annualMonths`
 * ("dos meses sin coste"; en copy NUNCA porcentajes ni tachados — handoff v2).
 * Ver también `web/precios/precios.component.ts`.
 */
export const SAAS_BASE_PLAN = {
  name: 'Esencial',
  /** € / mes del pack base (incluye los módulos de la capa Núcleo). */
  monthly: 45,
  /** Meses que se pagan al año (dos sin coste) → anual = mensual × 10. */
  annualMonths: 10,
};

// Categorías del CMS (type_key='modulos'). Núcleo = base incluida · Vertical =
// packs de sector (presentación comercial, no ítem à la carte) · el resto son
// categorías de módulos.
/** Categoría/capa del CMS que va incluida en el pack base. */
export const CORE_CATEGORY_RE = /n[úu]cleo|incluido/i;
/** Categoría de packs de sector: preseleccionan módulos, no se venden sueltos. */
export const VERTICAL_CATEGORY_RE = /vertical|pack/i;

/**
 * Precio estándar de un módulo (€/mes). Regla de clasificación à la carte
 * (fuera de Núcleo y Vertical), por PRECIO:
 *   - precio >= MODULE_PRICE → MÓDULO (funciona solo).
 *   - 0 < precio < MODULE_PRICE → COMPLEMENTO del módulo de su misma categoría.
 *   - precio 0 / vacío → va incluido (base).
 * Estandarizado en catálogo: módulo 8 €, complemento 3 €. Así un módulo y sus
 * complementos conviven en una MISMA categoría, sin subcategorías.
 */
export const MODULE_PRICE = 8;

export interface SectorPreset {
  id: string;
  label: string;
  /** Patrones que se comparan con el título del módulo para pre-marcarlo. */
  matchers: RegExp[];
}

/**
 * Plantillas de inicio por sector (sin precio especial): pre-marcan los módulos
 * que se suelen usar en cada sector. El usuario las ajusta libremente. El mapeo
 * va por palabra clave del título porque el CMS de módulos está plano (sin
 * relación sector↔módulo). Los módulos de Núcleo van incluidos y no se marcan.
 */
export const SECTOR_PRESETS: SectorPreset[] = [
  {
    id: 'servicios-tecnicos',
    label: 'Servicios técnicos',
    matchers: [/parte/i, /fichaj|rrhh/i],
  },
  {
    id: 'gestoria',
    label: 'Gestoría',
    matchers: [/gasto|escaneo|ocr|\bia\b|inteligencia/i, /documento/i],
  },
  {
    id: 'hosteleria',
    label: 'Hostelería',
    matchers: [/tpv|comand/i, /estad[íi]stic|restaurante/i, /producto|inventario/i],
  },
  {
    id: 'comercio',
    label: 'Comercio y almacén',
    matchers: [/producto|inventario|stock/i, /tpv|comand/i],
  },
  {
    id: 'autonomos',
    label: 'Autónomos y pymes',
    matchers: [/gasto|escaneo|ocr|\bia\b|inteligencia/i, /proyecto|tarea/i],
  },
  {
    id: 'seguros',
    label: 'Seguros',
    matchers: [/seguro|p[óo]liza/i, /documento/i],
  },
];

/**
 * Precio por usuarios. Los OPERATIVOS son ilimitados y van incluidos en el
 * pack base (la frontera con el completo es de permisos: el operativo ficha
 * y opera; facturar, ver el global y sacar informes exige usuario completo).
 * Solo se cobra por usuario completo adicional al incluido.
 */
export const SAAS_USERS = {
  /** Usuarios completos incluidos en el pack base. */
  includedFull: 1,
  /** €/mes por usuario completo adicional. */
  fullPrice: 12,
};
